import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Sparkles, X, Loader2, AlertTriangle, CheckCircle2, XCircle, Check, ArrowRight, RefreshCw, History, Info,
  ChevronDown, ChevronRight, MessageSquarePlus, ShieldCheck, Circle,
} from 'lucide-react';
import EliteContentDocument from './EliteContentDocument';

/**
 * EliteContentAiRevision — "AI Edit Content" for the Content Workspace
 * (27-Aug-2026). Same interaction model as Re-generate RoadMap: the admin
 * says what should change in their own words, the backend plans + revises
 * only the affected sections, and NOTHING is persisted until the admin has
 * seen the revision summary, the diff and the validation result and clicks
 * Apply Changes.
 *
 * API (all admin-gated server-side):
 *   POST .../content/topics/:id/revisions                 { instruction, mode, focusAreas, baseMarkdown?, expectedUpdatedAt?, parentRevisionId? }
 *   GET  .../content/topics/:id/revisions/:rid            poll while GENERATING (real stages), then the draft
 *   POST .../content/topics/:id/revisions/:rid/apply      { force? }  → 200 content · 409 current · 422 validation
 *   POST .../content/topics/:id/revisions/:rid/reject
 *   GET  .../content/topics/:id/revisions                 history
 */

export const FOCUS_AREAS = [
  ['architecture', 'Architecture'], ['examples', 'Examples'], ['code', 'Code'], ['troubleshooting', 'Troubleshooting'],
  ['interview', 'Interview'], ['knowledge_check', 'Knowledge Check'], ['conciseness', 'Conciseness'], ['accuracy', 'Accuracy'],
];
export const INSTRUCTION_MAX = 6000;
const POLL_MS = 2500;
const POLL_TIMEOUT_MS = 12 * 60 * 1000;

// Honest stage list — driven by the real `stage` the worker checkpoints.
export const STAGE_STEPS = [
  { key: 'planning', label: 'Analyzing your instructions', covers: ['queued', 'planning'] },
  { key: 'targeting', label: 'Identifying affected sections', covers: ['planning'] },
  { key: 'generating', label: 'Generating revision', covers: ['generating'] },
  { key: 'validating', label: 'Validating content', covers: ['validating'] },
  { key: 'scoring', label: 'Scoring quality', covers: ['scoring'] },
];
const STAGE_POS = { queued: 0.5, planning: 1, generating: 2, validating: 3, scoring: 4, done: 5, failed: 5 };
// [start, end] of the real stage each displayed step spans. Analyzing runs from
// queued through planning; Identifying is the planning call itself.
const STEP_SPAN = [[0.5, 1], [1, 1], [2, 2], [3, 3], [4, 4]];
export const stepState = (stage, stepIndex) => {
  if (stage === 'done') return 'done';
  const pos = STAGE_POS[stage] ?? 0.5;
  const [start, end] = STEP_SPAN[stepIndex];
  if (pos > end) return 'done';
  return pos >= start ? 'active' : 'pending';
};

// ─── document + diff helpers (client side, for the Changes view) ────────────
/** Split markdown into "## " sections, fence-aware — the same model the backend uses. */
export const splitSections = (markdown) => {
  const lines = String(markdown || '').replace(/\r\n?/g, '\n').split('\n');
  const out = []; let cur = null; let inFence = false; const preamble = [];
  for (const line of lines) {
    if (/^\s*(```|~~~)/.test(line)) inFence = !inFence;
    const m = !inFence && /^##\s+(?:(\d+)\.\s*)?(.+?)\s*#*\s*$/.exec(line);
    if (m) { cur = { number: m[1] ? parseInt(m[1], 10) : null, title: m[2].trim(), heading: line, lines: [] }; out.push(cur); } else if (cur) cur.lines.push(line); else preamble.push(line);
  }
  return { preamble: preamble.join('\n'), sections: out.map((s) => ({ ...s, body: s.lines.join('\n').replace(/\s+$/, '') })) };
};
const keyOf = (t) => String(t || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

/** Line diff (LCS) → ops [{type:'same'|'add'|'del', text}]. Bounded: very long inputs fall back to whole-block replace. */
export const lineDiff = (before, after, maxLines = 1500) => {
  const a = String(before || '').split('\n'); const b = String(after || '').split('\n');
  if (a.length > maxLines || b.length > maxLines) return [...a.map((text) => ({ type: 'del', text })), ...b.map((text) => ({ type: 'add', text }))];
  const n = a.length; const m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i -= 1) for (let j = m - 1; j >= 0; j -= 1) dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const ops = []; let i = 0; let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { ops.push({ type: 'same', text: a[i] }); i += 1; j += 1; } else if (dp[i + 1][j] >= dp[i][j + 1]) { ops.push({ type: 'del', text: a[i] }); i += 1; } else { ops.push({ type: 'add', text: b[j] }); j += 1; }
  }
  while (i < n) { ops.push({ type: 'del', text: a[i] }); i += 1; }
  while (j < m) { ops.push({ type: 'add', text: b[j] }); j += 1; }
  return ops;
};

/** Collapse long unchanged runs so the diff stays readable (keep 3 lines of context). */
const foldOps = (ops, context = 3) => {
  const out = []; let run = [];
  const flush = (atEnd) => {
    if (!run.length) return;
    const isStart = out.length === 0;
    const keepHead = isStart ? 0 : context; const keepTail = atEnd ? 0 : context;
    if (run.length <= keepHead + keepTail + 2) out.push(...run);
    else { out.push(...run.slice(0, keepHead)); out.push({ type: 'fold', count: run.length - keepHead - keepTail }); out.push(...run.slice(run.length - keepTail)); }
    run = [];
  };
  for (const op of ops) { if (op.type === 'same') run.push(op); else { flush(false); out.push(op); } }
  flush(true);
  return out;
};

/** Section-level pairing of two documents for the Changes view (mirrors the server's diff but carries the text). */
export const sectionPairs = (beforeMd, afterMd) => {
  const before = splitSections(beforeMd); const after = splitSections(afterMd);
  const beforeByKey = new Map(); before.sections.forEach((s) => { if (!beforeByKey.has(keyOf(s.title))) beforeByKey.set(keyOf(s.title), s); });
  const used = new Set(); const pairs = [];
  for (const s of after.sections) {
    const prev = beforeByKey.get(keyOf(s.title));
    if (!prev || used.has(prev)) { pairs.push({ title: s.title, status: 'added', before: null, after: s }); continue; }
    used.add(prev);
    const status = prev.body !== s.body ? 'modified' : prev.number !== s.number ? 'renumbered' : 'unchanged';
    pairs.push({ title: s.title, status, before: prev, after: s });
  }
  for (const s of before.sections) if (!used.has(s)) pairs.push({ title: s.title, status: 'removed', before: s, after: null });
  pairs.sort((x, y) => ((x.after || x.before).number || 0) - ((y.after || y.before).number || 0));
  if (before.preamble.trim() !== after.preamble.trim()) pairs.unshift({ title: 'Document title', status: 'modified', before: { body: before.preamble, number: 0 }, after: { body: after.preamble, number: 0 } });
  return pairs;
};

// ─── atoms (same visual language as the RoadMap regeneration modal) ────────
const Primary = ({ onClick, disabled, children, title, tone = 'violet', className = '', ...rest }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title} {...rest}
    className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-white text-sm shadow disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-500 ${className}`}
    style={{ background: tone === 'green' ? 'linear-gradient(135deg,#059669,#10B981)' : 'linear-gradient(135deg,#5B21B6,#7C3AED)' }}>
    {children}
  </button>
);
const Ghost = ({ onClick, disabled, children, title, danger, className = '', ...rest }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title} {...rest}
    className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm border transition disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500 ${danger ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-gray-200 text-gray-700 bg-gray-50 hover:bg-gray-100'} ${className}`}>
    {children}
  </button>
);
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const STATUS_TONE = { DRAFT: 'bg-violet-50 text-violet-700', GENERATING: 'bg-blue-50 text-blue-700', APPLIED: 'bg-emerald-50 text-emerald-700', REJECTED: 'bg-gray-100 text-gray-600', FAILED: 'bg-red-50 text-red-600', SUPERSEDED: 'bg-gray-100 text-gray-500' };
const STATUS_LABEL = { DRAFT: 'Draft', GENERATING: 'Generating', APPLIED: 'Applied', REJECTED: 'Rejected', FAILED: 'Failed', SUPERSEDED: 'Superseded' };

// ─── pieces ─────────────────────────────────────────────────────────────────
const StageList = ({ stage }) => (
  <ol className="space-y-2" aria-label="Revision progress" aria-live="polite">
    {STAGE_STEPS.map((step, i) => {
      const st = stepState(stage, i);
      return (
        <li key={step.key} className={`flex items-center gap-2 text-sm ${st === 'done' ? 'text-emerald-700' : st === 'active' ? 'text-violet-800 font-semibold' : 'text-gray-400'}`} data-stage-state={st}>
          {st === 'done' ? <CheckCircle2 size={16} className="text-emerald-500" /> : st === 'active' ? <Loader2 size={16} className="animate-spin text-violet-600" /> : <Circle size={16} />}
          {step.label}
        </li>
      );
    })}
  </ol>
);

const ScoreDelta = ({ before, after, qualityMin }) => {
  if (after == null && before == null) return null;
  const decreased = before != null && after != null && after < before;
  return (
    <div className={`p-3 rounded-lg border text-xs ${decreased ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
      <p className="font-bold uppercase tracking-wide text-[10px] mb-1">Quality score</p>
      <p className="text-base font-black inline-flex items-center gap-2">
        <span>{before ?? '—'}</span><ArrowRight size={14} /><span className={after != null && after < qualityMin ? 'text-amber-700' : ''}>{after ?? '—'}</span>
      </p>
      {decreased && <p className="mt-1 inline-flex items-center gap-1"><AlertTriangle size={12} /> Quality decreased after revision. Review recommended.</p>}
      {after == null && <p className="mt-1 text-gray-500">The score could not be recomputed for this revision.</p>}
    </div>
  );
};

const ValidationPanel = ({ validation }) => {
  if (!validation) return null;
  const critical = validation.critical || []; const warnings = validation.warnings || [];
  if (critical.length === 0 && warnings.length === 0) {
    return <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-semibold inline-flex items-center gap-2"><ShieldCheck size={14} /> Validation passed</div>;
  }
  return (
    <div className="space-y-2">
      {critical.length > 0 && (
        <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 text-xs" role="alert">
          <p className="font-bold mb-1 inline-flex items-center gap-1"><XCircle size={14} /> Cannot apply changes — {critical.length} critical issue{critical.length > 1 ? 's' : ''} need attention</p>
          <ul className="list-disc pl-5 space-y-0.5">{critical.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-xs">
          <p className="font-bold mb-1 inline-flex items-center gap-1"><AlertTriangle size={14} /> Validation issues found ({warnings.length})</p>
          <ul className="list-disc pl-5 space-y-0.5">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}
    </div>
  );
};

const SummaryPanel = ({ revision }) => {
  const s = revision.change_summary || {}; const requested = s.requested || []; const changed = s.changed || []; const preserved = s.preserved || [];
  return (
    <div className="grid gap-3 md:grid-cols-2 text-xs">
      <div className="p-3 rounded-lg border border-gray-200 bg-white">
        <p className="font-bold uppercase tracking-wide text-[10px] text-gray-500 mb-1">Changed ({changed.length})</p>
        {changed.length === 0 ? <p className="text-gray-500">No sections changed.</p> : (
          <ul className="space-y-0.5">
            {changed.map((c, i) => (
              <li key={i} className="flex items-start gap-1.5"><Check size={13} className="text-emerald-500 mt-0.5 shrink-0" />
                <span><span className="capitalize text-gray-500">{c.status}</span> §{c.number} {c.title}{typeof c.wordsDelta === 'number' && c.wordsDelta !== 0 ? <span className="text-gray-400"> ({c.wordsDelta > 0 ? '+' : ''}{c.wordsDelta} words)</span> : null}</span>
              </li>
            ))}
          </ul>
        )}
        {requested.length > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="font-bold uppercase tracking-wide text-[10px] text-gray-500 mb-1">Requested</p>
            <ul className="space-y-0.5">
              {requested.map((r, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  {r.status === 'applied' ? <Check size={13} className="text-emerald-500 mt-0.5 shrink-0" /> : <AlertTriangle size={13} className="text-amber-500 mt-0.5 shrink-0" />}
                  <span>{r.summary}{r.status !== 'applied' ? <span className="text-amber-700"> — no change detected</span> : null}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="p-3 rounded-lg border border-gray-200 bg-white">
        <p className="font-bold uppercase tracking-wide text-[10px] text-gray-500 mb-1">Preserved ({preserved.length})</p>
        <p className="text-gray-600 mb-1">{preserved.length} section{preserved.length === 1 ? '' : 's'} unchanged{preserved.some((p) => p.renumbered) ? ` (${preserved.filter((p) => p.renumbered).length} renumbered)` : ''}, including learning objectives, existing examples and structure.</p>
        <p className="text-gray-500 line-clamp-3">{preserved.slice(0, 8).map((p) => p.title).join(' · ')}{preserved.length > 8 ? ' …' : ''}</p>
        <p className="mt-2 pt-2 border-t border-gray-100 text-gray-600"><b>Estimated impact:</b> {s.impact?.changed ?? changed.length} section{(s.impact?.changed ?? changed.length) === 1 ? '' : 's'} changed, {s.impact?.preserved ?? preserved.length} unchanged · {s.impact?.wordsBefore?.toLocaleString?.() ?? '—'} → {s.impact?.wordsAfter?.toLocaleString?.() ?? '—'} words</p>
      </div>
    </div>
  );
};

const DiffSection = ({ pair, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const number = (pair.after || pair.before).number;
  const tone = { modified: 'text-violet-700 bg-violet-50', added: 'text-emerald-700 bg-emerald-50', removed: 'text-red-700 bg-red-50', renumbered: 'text-gray-600 bg-gray-100', unchanged: 'text-gray-500 bg-gray-50' }[pair.status];
  const ops = useMemo(() => {
    if (!open) return [];
    if (pair.status === 'added') return pair.after.body.split('\n').map((text) => ({ type: 'add', text }));
    if (pair.status === 'removed') return pair.before.body.split('\n').map((text) => ({ type: 'del', text }));
    if (pair.status === 'modified') return foldOps(lineDiff(pair.before.body, pair.after.body));
    return [];
  }, [open, pair]);
  const inert = pair.status === 'unchanged' || pair.status === 'renumbered';
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden" data-diff-status={pair.status}>
      <button type="button" onClick={() => !inert && setOpen((o) => !o)} aria-expanded={open} disabled={inert}
        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-bold ${inert ? 'text-gray-500 cursor-default' : 'text-gray-800 hover:bg-gray-50'}`}>
        {inert ? <span className="w-[14px]" /> : open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${tone}`}>{pair.status}</span>
        <span className="truncate">{number ? `§${number} ` : ''}{pair.title}</span>
        {pair.status === 'renumbered' && <span className="text-gray-400 font-normal">§{pair.before.number} → §{pair.after.number}</span>}
      </button>
      {open && (
        <pre className="m-0 p-0 text-[12px] leading-5 font-mono overflow-x-auto bg-white border-t border-gray-100" aria-label={`Changes in ${pair.title}`}>
          {ops.map((op, i) => op.type === 'fold'
            ? <div key={i} className="px-3 text-gray-400 bg-gray-50 italic">… {op.count} unchanged lines …</div>
            : <div key={i} className={`px-3 whitespace-pre-wrap break-words ${op.type === 'add' ? 'bg-emerald-50 text-emerald-900' : op.type === 'del' ? 'bg-red-50 text-red-900 line-through decoration-red-300' : 'text-gray-700'}`}>
                <span className="select-none inline-block w-4 text-gray-400">{op.type === 'add' ? '+' : op.type === 'del' ? '−' : ' '}</span>{op.text || ' '}
              </div>)}
        </pre>
      )}
    </div>
  );
};

const ChangesView = ({ before, after }) => {
  const pairs = useMemo(() => sectionPairs(before, after), [before, after]);
  const changed = pairs.filter((p) => ['modified', 'added', 'removed'].includes(p.status));
  const rest = pairs.filter((p) => !['modified', 'added', 'removed'].includes(p.status));
  const [showRest, setShowRest] = useState(false);
  return (
    <div className="space-y-2">
      {changed.length === 0 && <p className="text-xs text-gray-500">No differences.</p>}
      {changed.map((p, i) => <DiffSection key={`${p.title}-${i}`} pair={p} defaultOpen={i < 3} />)}
      {rest.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowRest((o) => !o)} className="text-xs font-bold text-gray-500 hover:text-gray-800 inline-flex items-center gap-1" aria-expanded={showRest}>
            {showRest ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {rest.length} unchanged section{rest.length === 1 ? '' : 's'}
          </button>
          {showRest && <div className="mt-2 space-y-1">{rest.map((p, i) => <DiffSection key={`${p.title}-${i}`} pair={p} />)}</div>}
        </div>
      )}
    </div>
  );
};

// ─── the dialog ─────────────────────────────────────────────────────────────
/**
 * Props
 *   open, onClose
 *   api(path, opts)         the workspace's fetch wrapper (auth header, timeout, error shape)
 *   topicPath               `/api/admin/elite-technologies/:slug/content/topics/:id`
 *   content                 the live row (updated_at, quality_score, …)
 *   draft, savedMarkdown    editor state — used to offer "current draft vs last saved"
 *   initialRevision         reopen an existing draft/generating revision (from history or the background banner)
 *   qualityMin
 *   onApplied(content, revision)   the revision became the live content
 *   onReloadLatest()        admin chose to reload after a conflict
 *   onBackground(revision)  admin sent generation to the background
 *   toast(message, type)
 */
export const AiRevisionDialog = ({ open, onClose, api, topicPath, content, draft, savedMarkdown, initialRevision, qualityMin = 70, onApplied, onReloadLatest, onBackground, toast }) => {
  const isDirty = draft !== savedMarkdown;
  const [phase, setPhase] = useState('compose');           // base | compose | generating | ready | error
  const [baseChoice, setBaseChoice] = useState('draft');
  const [instruction, setInstruction] = useState('');
  const [focus, setFocus] = useState([]);
  const [mode, setMode] = useState('improve');
  const [revision, setRevision] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('changes');
  const [busy, setBusy] = useState(false);
  const [refineOf, setRefineOf] = useState(null);           // revision being refined further
  const [conflict, setConflict] = useState(null);           // { current } from a 409 on apply
  const [showConflictDiff, setShowConflictDiff] = useState(false);
  const inFlight = useRef(false);
  const pollTimer = useRef(null);
  const ref = useRef(null);

  // (re)initialise whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    setError(null); setBusy(false); setConflict(null); setShowConflictDiff(false); setTab('changes'); setRefineOf(null);
    if (initialRevision) {
      setRevision(initialRevision);
      setPhase(initialRevision.status === 'GENERATING' ? 'generating' : initialRevision.status === 'FAILED' ? 'error' : 'ready');
      if (initialRevision.status === 'FAILED') setError(initialRevision.error || 'Revision failed');
      setInstruction(initialRevision.status === 'FAILED' ? initialRevision.instruction || '' : '');
    } else {
      setRevision(null); setInstruction(''); setFocus([]); setMode('improve'); setBaseChoice('draft');
      setPhase(isDirty ? 'base' : 'compose');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialRevision]);

  // Esc closes (not while applying)
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey);
    const first = ref.current && ref.current.querySelector('textarea, button:not([disabled]), input');
    if (first) first.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onClose, phase]);

  // poll while generating
  const stopPolling = useCallback(() => { if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; } }, []);
  useEffect(() => () => stopPolling(), [stopPolling]);
  useEffect(() => {
    if (!open || phase !== 'generating' || !revision || revision.status !== 'GENERATING') return undefined;
    const startedAt = Date.now();
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const data = await api(`${topicPath}/revisions/${revision.id}`);
        if (cancelled) return;
        const r = data.revision;
        setRevision(r);
        if (r.status === 'GENERATING') {
          if (Date.now() - startedAt > POLL_TIMEOUT_MS) { setError('The revision is taking longer than expected. Your content has not been changed — check Revision history later or try again.'); setPhase('error'); return; }
          pollTimer.current = setTimeout(tick, POLL_MS);
        } else if (r.status === 'DRAFT') { setPhase('ready'); }
        else { setError(r.error || `Revision ${STATUS_LABEL[r.status] || r.status}.`); setPhase('error'); }
      } catch (e) {
        if (cancelled) return;
        // transient network trouble: keep polling, the worker keeps working
        pollTimer.current = setTimeout(tick, POLL_MS * 2);
      }
    };
    pollTimer.current = setTimeout(tick, POLL_MS);
    return () => { cancelled = true; stopPolling(); };
  }, [open, phase, revision, api, topicPath, stopPolling]);

  const instructionOk = instruction.trim().length >= 3;
  const toggleFocus = (k) => setFocus((f) => (f.includes(k) ? f.filter((x) => x !== k) : [...f, k]));

  const generate = async () => {
    if (inFlight.current || !instructionOk) return;
    inFlight.current = true; setBusy(true); setError(null);
    const body = { instruction: instruction.trim(), mode, focusAreas: focus, expectedUpdatedAt: content?.updated_at || null };
    if (refineOf) body.parentRevisionId = refineOf.id;
    else if (isDirty && baseChoice === 'draft') body.baseMarkdown = draft;
    try {
      const data = await api(`${topicPath}/revisions`, { method: 'POST', body: JSON.stringify(body), timeoutMs: 6 * 60 * 1000 });
      setRevision(data.revision);
      if (data.async || data.revision.status === 'GENERATING') setPhase('generating');
      else if (data.revision.status === 'DRAFT') setPhase('ready');
      else { setError(data.revision.error || 'Revision failed'); setPhase('error'); }
    } catch (e) {
      if (e.status === 409 && e.data?.revision) { setRevision(e.data.revision); setPhase('generating'); }
      else if (e.status === 409 && e.data?.current) { setError('This topic was modified by someone else since you opened it. Reload the latest version before generating a revision.'); setConflict({ current: e.data.current, stage: 'generate' }); setPhase('error'); }
      else { setError(e.message || 'Could not generate a revision.'); if (e.data?.revision) setRevision(e.data.revision); setPhase('error'); }
    } finally { inFlight.current = false; setBusy(false); }
  };

  const apply = async ({ force = false } = {}) => {
    if (!revision || busy) return;
    setBusy(true); setError(null);
    try {
      const data = await api(`${topicPath}/revisions/${revision.id}/apply`, { method: 'POST', body: JSON.stringify({ force }) });
      onApplied(data.content, data.revision, data.version);
    } catch (e) {
      if (e.status === 409 && e.data?.current) { setConflict({ current: e.data.current, stage: 'apply' }); }
      else if (e.status === 422) { setError(e.message); setRevision((r) => (r && e.data?.validation ? { ...r, validation: e.data.validation } : r)); }
      else setError(e.message || 'Could not apply changes.');
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!revision || busy) return;
    setBusy(true);
    try { await api(`${topicPath}/revisions/${revision.id}/reject`, { method: 'POST' }); toast && toast('Revision rejected. Your content is unchanged.'); onClose(); }
    catch (e) { setError(e.message || 'Could not reject the revision.'); }
    finally { setBusy(false); }
  };

  const refine = () => { setRefineOf(revision); setInstruction(''); setFocus([]); setPhase('compose'); };
  const backToCompose = () => { setPhase(isDirty && !refineOf ? 'base' : 'compose'); setError(null); };

  if (!open) return null;
  const critical = (revision?.validation?.critical || []).length;
  const wide = phase === 'ready';
  const title = phase === 'ready' ? 'Revision Ready' : phase === 'generating' ? 'Regenerating Content' : refineOf ? 'Request Further Changes' : 'Improve Content';

  return (
    <div className="fixed inset-0 z-[125] flex items-center justify-center p-3 sm:p-4 bg-black/50" role="presentation" data-testid="ai-revision-dialog">
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby="ai-revision-title" className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-5xl' : 'max-w-2xl'} max-h-[92vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="p-5">
          <div className="flex items-start justify-between mb-1">
            <h2 id="ai-revision-title" className="text-lg font-black text-gray-900 inline-flex items-center gap-2"><Sparkles size={18} className="text-violet-600" /> {title}</h2>
            <button type="button" onClick={onClose} disabled={busy} className="text-gray-600 hover:text-gray-900 disabled:opacity-40" aria-label="Close"><X size={18} /></button>
          </div>

          {/* ── base choice (unsaved manual changes) ── */}
          {phase === 'base' && (
            <>
              <p className="text-xs text-gray-700 mb-3">You have unsaved manual changes. AI regeneration can use your current draft or the last saved version.</p>
              <fieldset className="mb-4">
                <legend className="sr-only">Content to revise</legend>
                {[['draft', 'Use current draft', 'What you see in the editor right now (recommended).'], ['saved', 'Use last saved version', 'Ignores your unsaved edits for this revision. They stay in the editor until you apply.']].map(([v, l, h]) => (
                  <label key={v} className="flex items-start gap-2 mb-1.5 cursor-pointer">
                    <input type="radio" name="aiRevisionBase" value={v} checked={baseChoice === v} onChange={() => setBaseChoice(v)} className="mt-0.5" />
                    <span><span className="text-xs font-semibold text-gray-800">{l}</span><span className="block text-[10px] text-gray-600">{h}</span></span>
                  </label>
                ))}
              </fieldset>
              <div className="flex gap-2"><Ghost onClick={onClose} className="flex-1">Cancel</Ghost><Primary onClick={() => setPhase('compose')} className="flex-1">Continue <ArrowRight size={14} /></Primary></div>
            </>
          )}

          {/* ── compose ── */}
          {phase === 'compose' && (
            <>
              <p className="text-xs text-gray-700 mb-4">
                {refineOf ? <>Describe what should change in <b>revision {refineOf.revision_no}</b>. The next revision builds on the current draft; you will still review the full difference against the original.</> : 'Tell AI what should be changed. Sections you do not mention are preserved as they are.'}
              </p>
              {error && (
                <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs rounded mb-3" role="alert">
                  <p className="font-bold mb-0.5 inline-flex items-center gap-1"><AlertTriangle size={12} /> Revision failed</p>{error}
                  <p className="mt-1 text-red-600">Your content has not been changed. You can adjust the instruction and try again.</p>
                </div>
              )}
              <label htmlFor="ai-revision-instruction" className="block text-[11px] font-bold text-gray-700 uppercase tracking-wide mb-1">What should be changed?</label>
              <textarea id="ai-revision-instruction" autoFocus rows={5} value={instruction} onChange={(e) => setInstruction(e.target.value)} maxLength={INSTRUCTION_MAX} disabled={busy}
                placeholder="Example: The networking section is too shallow. Add production troubleshooting examples and explain common failure scenarios. Preserve the existing structure and examples that are already good."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs mb-1 disabled:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-300" />
              <p className="text-[10px] text-gray-600 mb-4">{instruction.length}/{INSTRUCTION_MAX}</p>

              <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wide mb-2">Optional guidance</p>
              <p className="text-[10px] text-gray-600 mb-1">Focus areas</p>
              <div className="flex flex-wrap gap-1.5 mb-4" role="group" aria-label="Focus areas">
                {FOCUS_AREAS.map(([k, label]) => (
                  <button key={k} type="button" onClick={() => toggleFocus(k)} aria-pressed={focus.includes(k)} disabled={busy}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition ${focus.includes(k) ? 'bg-violet-600 border-violet-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>{label}</button>
                ))}
              </div>
              <fieldset className="mb-4" disabled={busy}>
                <legend className="text-[11px] font-bold text-gray-700 uppercase tracking-wide mb-1">Regeneration behavior</legend>
                {[['improve', 'Improve existing content', 'Smallest necessary changes to satisfy your instructions. Everything else is preserved.'], ['rebuild', 'Rebuild content using instructions', 'Allows restructuring and substantial rewriting, still using the current content as source material.']].map(([v, l, h]) => (
                  <label key={v} className="flex items-start gap-2 mb-1.5 cursor-pointer">
                    <input type="radio" name="aiRevisionMode" value={v} checked={mode === v} onChange={() => setMode(v)} className="mt-0.5" />
                    <span><span className="text-xs font-semibold text-gray-800">{l}</span><span className="block text-[10px] text-gray-600">{h}</span></span>
                  </label>
                ))}
              </fieldset>
              {isDirty && !refineOf && <p className="text-[10px] text-gray-500 mb-3 inline-flex items-center gap-1"><Info size={11} /> Revising {baseChoice === 'draft' ? 'your current draft' : 'the last saved version'}. <button type="button" className="underline" onClick={() => setPhase('base')}>Change</button></p>}
              <div className="flex gap-2">
                <Ghost onClick={refineOf ? () => { setRefineOf(null); setPhase('ready'); } : onClose} disabled={busy} className="flex-1">{refineOf ? 'Back to revision' : 'Cancel'}</Ghost>
                <Primary onClick={generate} disabled={busy || !instructionOk} title={instructionOk ? '' : 'Describe what should change first'} className="flex-1">
                  {busy ? <><Loader2 size={15} className="animate-spin" /> Generating…</> : <><Sparkles size={15} /> Generate Revision</>}
                </Primary>
              </div>
            </>
          )}

          {/* ── generating ── */}
          {phase === 'generating' && (
            <>
              <p className="text-xs text-gray-700 mb-4">Your current content stays exactly as it is until you apply the revision. You can keep reading or editing while this runs.</p>
              <div className="p-4 rounded-xl bg-violet-50 border border-violet-100 mb-4"><StageList stage={revision?.stage} /></div>
              <div className="flex gap-2">
                <Ghost onClick={() => { onBackground && onBackground(revision); onClose(); }} className="flex-1">Continue in background</Ghost>
              </div>
            </>
          )}

          {/* ── ready ── */}
          {phase === 'ready' && revision && (
            <>
              <p className="text-xs text-gray-700 mb-3">Revision {revision.revision_no}{revision.parent_revision_id ? ' (refined)' : ''} for “{revision.instruction}”. Review what changed, then apply, refine or reject. Nothing has been saved yet.</p>
              {error && <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs rounded mb-3" role="alert">{error}</div>}
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_260px] mb-3">
                <SummaryPanel revision={revision} />
                <div className="space-y-2">
                  <ScoreDelta before={revision.quality_score_before} after={revision.quality_score_after} qualityMin={qualityMin} />
                  <ValidationPanel validation={revision.validation} />
                </div>
              </div>
              <div className="flex items-center gap-1 border-b border-gray-200 mb-3" role="tablist" aria-label="Review changes">
                {[['changes', 'Changes'], ['before', 'Before'], ['after', 'After']].map(([k, l]) => (
                  <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={`px-3 py-2 text-xs font-bold border-b-2 -mb-px ${tab === k ? 'border-violet-600 text-violet-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{l}</button>
                ))}
              </div>
              <div className="max-h-[45vh] overflow-y-auto rounded-lg border border-gray-100 p-3 bg-gray-50/50" role="tabpanel">
                {tab === 'changes' && <ChangesView before={revision.base_markdown || ''} after={revision.candidate_markdown || ''} />}
                {tab === 'before' && <div className="bg-white rounded-lg p-4"><EliteContentDocument markdown={revision.base_markdown || ''} /></div>}
                {tab === 'after' && <div className="bg-white rounded-lg p-4"><EliteContentDocument markdown={revision.candidate_markdown || ''} /></div>}
              </div>
              <div className="flex flex-wrap gap-2 mt-4 justify-end">
                <Ghost danger onClick={reject} disabled={busy}>Reject</Ghost>
                <Ghost onClick={refine} disabled={busy}><MessageSquarePlus size={14} /> Request Further Changes</Ghost>
                <Primary onClick={() => apply()} disabled={busy || critical > 0} title={critical > 0 ? `${critical} critical issue(s) must be resolved first` : 'Persist this revision as the current content'}>
                  {busy ? <><Loader2 size={15} className="animate-spin" /> Applying Changes…</> : <><CheckCircle2 size={15} /> Apply Changes</>}
                </Primary>
              </div>
            </>
          )}

          {/* ── error ── */}
          {phase === 'error' && (
            <>
              <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs rounded mb-3" role="alert">
                <p className="font-bold mb-0.5 inline-flex items-center gap-1"><AlertTriangle size={12} /> Revision could not be generated</p>
                {error}
                <p className="mt-1 text-red-600">Your content has not been changed.</p>
              </div>
              <div className="flex gap-2">
                <Ghost onClick={onClose} className="flex-1">Close</Ghost>
                {conflict?.stage === 'generate'
                  ? <Primary onClick={() => { onReloadLatest && onReloadLatest(); onClose(); }} className="flex-1"><RefreshCw size={14} /> Reload Latest</Primary>
                  : <Primary onClick={backToCompose} className="flex-1"><RefreshCw size={14} /> Try again</Primary>}
              </div>
            </>
          )}
        </div>

        {/* ── conflict on apply ── */}
        {conflict && conflict.stage === 'apply' && (
          <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/40" role="presentation">
            <div role="alertdialog" aria-modal="true" aria-labelledby="ai-conflict-title" className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5">
              <h3 id="ai-conflict-title" className="text-base font-black text-gray-900 mb-1 inline-flex items-center gap-2"><AlertTriangle size={16} className="text-amber-500" /> Content Changed</h3>
              <p className="text-sm text-gray-700 mb-1">This topic was updated by another user{conflict.current?.review_notes?.lastEdit?.by ? ` (${conflict.current.review_notes.lastEdit.by})` : ''} at {fmtWhen(conflict.current?.updated_at)} after you opened it.</p>
              <p className="text-xs text-gray-500 mb-3">Your generated revision is based on an older version. Reload to see their changes (you can generate again from the latest content), review the difference, or overwrite their version with this revision.</p>
              {showConflictDiff && (
                <div className="max-h-[40vh] overflow-y-auto rounded-lg border border-gray-100 p-3 bg-gray-50/50 mb-3">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">Their current version vs the version your revision was based on</p>
                  <ChangesView before={revision?.base_markdown || ''} after={conflict.current?.content_markdown || ''} />
                </div>
              )}
              <div className="flex flex-wrap gap-2 justify-end">
                <Ghost onClick={() => setConflict(null)}>Cancel</Ghost>
                <Ghost onClick={() => setShowConflictDiff((o) => !o)}>{showConflictDiff ? 'Hide Difference' : 'Review Difference'}</Ghost>
                <Ghost onClick={() => { onReloadLatest && onReloadLatest(); onClose(); }}><RefreshCw size={14} /> Reload Latest</Ghost>
                <Primary onClick={() => { setConflict(null); apply({ force: true }); }} disabled={busy}>Overwrite with this revision</Primary>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── history ────────────────────────────────────────────────────────────────
export const RevisionHistoryDialog = ({ open, onClose, api, topicPath, onOpenRevision }) => {
  const [rows, setRows] = useState(null); const [error, setError] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setRows(null); setError(null);
    api(`${topicPath}/revisions`).then((d) => { if (!cancelled) setRows(d.revisions || []); }).catch((e) => { if (!cancelled) setError(e.message || 'Could not load history'); });
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => { cancelled = true; document.removeEventListener('keydown', onKey); };
  }, [open, api, topicPath, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[125] flex items-center justify-center p-4 bg-black/50" role="presentation" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="ai-history-title" className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <h2 id="ai-history-title" className="text-lg font-black text-gray-900 inline-flex items-center gap-2"><History size={18} /> Revision history</h2>
          <button type="button" onClick={onClose} className="text-gray-600 hover:text-gray-900" aria-label="Close"><X size={18} /></button>
        </div>
        {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
        {rows === null && !error && <p className="text-xs text-gray-500 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>}
        {rows && rows.length === 0 && <p className="text-xs text-gray-500">No AI revisions yet. Version 1 is the generated content.</p>}
        {rows && rows.length > 0 && (
          <ul className="space-y-2">
            {rows.map((r) => (
              <li key={r.id} className="p-3 rounded-lg border border-gray-200 text-xs">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_TONE[r.status] || 'bg-gray-100 text-gray-600'}`}>{STATUS_LABEL[r.status] || r.status}</span>
                  <b className="text-gray-800">{r.content_version ? `Version ${r.content_version}` : `Revision ${r.revision_no}`}</b>
                  <span className="text-gray-500">{r.mode === 'rebuild' ? 'Rebuild' : 'Improve'} · {r.created_by || 'admin'} · {fmtWhen(r.created_at)}</span>
                  {r.quality_score_before != null && r.quality_score_after != null && <span className="text-gray-500">score {r.quality_score_before} → {r.quality_score_after}</span>}
                  {r.change_summary?.impact && <span className="text-gray-500">{r.change_summary.impact.changed} changed / {r.change_summary.impact.preserved} preserved</span>}
                </div>
                <p className="text-gray-700">“{r.instruction}”</p>
                {r.error && <p className="text-red-600 mt-1">{r.error}</p>}
                {(r.status === 'DRAFT' || r.status === 'GENERATING') && <button type="button" onClick={() => onOpenRevision(r)} className="mt-1 font-bold text-violet-700 underline">{r.status === 'DRAFT' ? 'Review changes' : 'View progress'}</button>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AiRevisionDialog;
